import { cn } from "@/lib/utils";
import { thumbUrl } from "@/lib/image-thumb";
import { useUserBadge } from "@/hooks/useUserBadges";
import primeiroSvg from "@/assets/frames/primeiro.svg";
import segundoSvg from "@/assets/frames/segundo.svg";
import terceiroSvg from "@/assets/frames/terceiro.svg";
import proAsset from "@/assets/frames/pro.svg.asset.json";
const embaixadorSvg = proAsset.url;


/**
 * Avatar with rank/Embaixador frame overlay.
 * - 1st/2nd/3rd on the global leaderboard → medal frame
 * - Premium (Embaixador) → embaixador frame
 * - Rank takes priority over Embaixador when both apply.
 * The avatar image sits inside the frame's circular area.
 */
export function FramedAvatar({
  userId,
  src,
  alt = "",
  size = 48,
  className,
  innerClassName,
  isPremium: isPremiumProp,
  rank: rankProp,
  username,
}: {
  userId?: string | null;
  src?: string | null;
  alt?: string;
  size?: number;
  className?: string;
  innerClassName?: string;
  isPremium?: boolean;
  rank?: number | null;
  username?: string | null;
}) {
  const badge = useUserBadge(userId, {
    isPremium: isPremiumProp,
    rank: rankProp ?? null,
  });

  const avatarSrc =
    thumbUrl(src, size * 2) ||
    (username
      ? `https://api.dicebear.com/7.x/initials/svg?seed=${username}`
      : undefined);

  // Each frame SVG has a circular slot at a different position/size.
  // Values below are derived from the SVG geometry so the avatar slots in perfectly.
  const rankFrame =
    badge.rank === 1
      ? { src: primeiroSvg, top: "14.35%", left: "14.35%", size: "71.77%" }
      : badge.rank === 2
        ? { src: segundoSvg, top: "14.35%", left: "14.35%", size: "71.77%" }
        : badge.rank === 3
          ? { src: terceiroSvg, top: "14.35%", left: "14.35%", size: "71.77%" }
          : null;

  // Embaixador: no shape overlay, just a colored ring on the avatar.
  const premiumRing = !rankFrame && badge.isPremium;

  if (!rankFrame) {
    return (
      <img loading="lazy" decoding="async"
        src={avatarSrc}
        alt={alt}
        className={cn("rounded-full bg-muted object-cover", innerClassName, className)}
        style={{
          width: size,
          height: size,
          ...(premiumRing
            ? { border: "1.5px solid #9231FF", boxSizing: "border-box" }
            : {}),
        }}
      />
    );
  }

  return (
    <div
      className={cn("relative inline-block shrink-0", className)}
      style={{ width: size, height: size }}
    >
      <img loading="lazy" decoding="async"
        src={avatarSrc}
        alt={alt}
        className={cn("absolute rounded-full bg-muted object-cover", innerClassName)}
        style={{
          top: rankFrame.top,
          left: rankFrame.left,
          width: rankFrame.size,
          height: rankFrame.size,
        }}
      />
      <img loading="lazy" decoding="async"
        src={rankFrame.src}
        alt=""
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full select-none"
      />
    </div>
  );
}

export default FramedAvatar;
